import { useEffect, useState } from 'react';
import {
  COMPARISON_GROUP_HINTS,
  COMPARISON_GROUP_LABELS,
  COMPARISON_GROUPS,
  COMPARISON_METRICS,
  centsToPesos,
  type ComparisonGroupId,
  type ComparisonMetricUnit,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Button, Card, ErrorBanner, Spinner } from '../../components/ui';
import { Icon } from '../../components/Icon';

/**
 * Phase 2G.4 — Branch Operational & Business Comparison.
 *
 * Pure read of `GET /api/branch-comparison` (admin-only, server-side
 * branch scope). One card per metric group, one column per metric, one row
 * per branch plus an element-wise totals row. Figures are FACTS aggregated
 * from records the API already enforces — this view computes no score,
 * rank, grade or winner; interpretation stays with the Admin.
 *
 * Two metric bases are labelled explicitly so no number can be misread:
 * "in period" metrics honour the Manila date filter, "now" metrics are a
 * server-clock snapshot the date filter deliberately does not apply to.
 */

type GroupValues = Record<string, number>;

interface ComparisonRowDto {
  branch: { id: string; code: string; name: string; isActive: boolean };
  transactions: GroupValues;
  workload: GroupValues;
  pickup: GroupValues;
  delivery: GroupValues;
  value: GroupValues;
  discrepancies: GroupValues;
  exceptions: GroupValues;
  inventory: GroupValues;
  purchases: GroupValues;
  expenses: GroupValues;
}

interface ComparisonResponseDto {
  timeZone: string;
  range: { from: string | null; to: string | null } | null;
  generatedAt: string;
  truncated: boolean;
  branches: ComparisonRowDto[];
  totals: Record<ComparisonGroupId, GroupValues>;
}

const inputCls =
  'h-10 rounded-xl border border-slate-200 bg-panel px-3 text-sm text-slate-800 outline-none transition-colors focus:border-indigo-400';

/** Manila calendar date for the "Today" shortcut (server re-parses it as Manila). */
function manilaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());
}

/** One number, rendered per unit: centsToPesos already carries the ₱ sign
 *  and thousands grouping; deltas show an explicit sign. */
function formatValue(value: number, unit: ComparisonMetricUnit): string {
  if (unit === 'pesos') return centsToPesos(value);
  if (unit === 'delta') return value > 0 ? `+${value}` : String(value);
  return String(value);
}

export function ComparisonPage() {
  const { branchFilter } = useAuth();

  const [data, setData] = useState<ComparisonResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // View filters — every one is re-resolved server-side on each request.
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (fromFilter) params.set('from', fromFilter);
    if (toFilter) params.set('to', toFilter);

    void api
      .get<ComparisonResponseDto>(`/branch-comparison?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load the comparison.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, fromFilter, toFilter, refreshKey]);

  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;
  const hasFilters = Boolean(fromFilter || toFilter);

  const metricsOf = (group: ComparisonGroupId) =>
    COMPARISON_METRICS.filter((metric) => metric.group === group);

  const cellFor = (row: ComparisonRowDto | ComparisonResponseDto['totals'], group: ComparisonGroupId, key: string): number => {
    const groupValues = (row as unknown as Record<string, GroupValues>)[group];
    return groupValues?.[key] ?? 0;
  };

  return (
    <section className="space-y-4" aria-label="Branch comparison">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Branch Comparison</h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · operational and business figures side by side — read-only, from records
            the system already keeps.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setRefreshKey((k) => k + 1)}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="flex items-start gap-3 p-4">
        <Icon name="shield" className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <p className="text-sm text-slate-500">
          These are <span className="font-semibold text-slate-700">facts, not rankings</span> —
          this view never computes scores, grades or “best branch” labels; every number is a raw
          aggregate of recorded work and Admin interprets it. Metrics marked{' '}
          <span className="font-semibold text-slate-700">in period</span> honour the selected
          Manila dates; metrics marked{' '}
          <span className="font-semibold text-slate-700">now</span> are current state the date
          filter does not apply to. Viewing never changes any record.
        </p>
      </Card>

      {error && <ErrorBanner message={error} />}

      {/* Filters — branch comes from the header switcher; dates re-validated server-side. */}
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          From
          <input
            type="date"
            value={fromFilter}
            onChange={(e) => setFromFilter(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          To
          <input
            type="date"
            value={toFilter}
            onChange={(e) => setToFilter(e.target.value)}
            className={inputCls}
          />
        </label>
        <Button
          variant="secondary"
          onClick={() => {
            setFromFilter(manilaToday());
            setToFilter(manilaToday());
          }}
        >
          Today
        </Button>
        {hasFilters && (
          <Button
            variant="secondary"
            onClick={() => {
              setFromFilter('');
              setToFilter('');
            }}
          >
            Clear filters
          </Button>
        )}
        <span className="ml-auto text-xs font-medium text-slate-400">
          Dates are Manila business dates (Asia/Manila)
          {data?.range ? ` · ${data.range.from ?? '…'} → ${data.range.to ?? '…'}` : ' · all recorded history'}
        </span>
      </Card>

      {data?.truncated && (
        <Card className="flex items-start gap-3 border-amber-200 bg-amber-50 p-4">
          <Icon name="alert" className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">
            The exception scan reached its bound, so “Needs attention” counts are partial (oldest
            detections shown first). Every other figure is a complete aggregate.
          </p>
        </Card>
      )}

      {loading && !data ? (
        <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
          <Spinner className="h-5 w-5 text-indigo-600" /> Loading comparison…
        </div>
      ) : !data || data.branches.length === 0 ? (
        <Card className="flex flex-col items-center p-6 text-center">
          <Icon name="reports" className="h-8 w-8 text-slate-300" />
          <p className="mt-2 font-medium text-slate-700">No branches to compare.</p>
          <p className="mt-1 text-sm text-slate-500">
            Branches appear here as soon as they exist; each card fills with real figures from
            recorded work.
          </p>
        </Card>
      ) : (
        <>
          {/* Metadata line: server clock, scope, range. */}
          <p className="text-xs font-medium text-slate-400">
            Generated {new Date(data.generatedAt).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' })}{' '}
            ({data.timeZone}) · {data.branches.length} branch{data.branches.length === 1 ? '' : 'es'}
          </p>

          {COMPARISON_GROUPS.map((group) => {
            const metrics = metricsOf(group);
            return (
              <div key={group}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="text-sm font-semibold text-slate-700">
                    {COMPARISON_GROUP_LABELS[group]}
                  </h2>
                  <p className="text-xs text-slate-500">{COMPARISON_GROUP_HINTS[group]}</p>
                </div>
                <div className="mt-2 overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                      <tr>
                        <th className="px-4 py-3">Branch</th>
                        {metrics.map((metric) => (
                          <th key={metric.key} className="px-3 py-3 text-right">
                            {metric.label}
                            <span className="mt-0.5 block text-[10px] font-normal normal-case tracking-normal text-slate-400">
                              {metric.basis === 'window' ? 'in period' : 'now'}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.branches.map((row) => (
                        <tr key={row.branch.id} className="hover:bg-slate-100/70">
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">
                              {row.branch.code}
                              {!row.branch.isActive && (
                                <span className="ml-2 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold uppercase text-slate-500">
                                  inactive
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-slate-500">{row.branch.name}</p>
                          </td>
                          {metrics.map((metric) => (
                            <td
                              key={metric.key}
                              className="px-3 py-3 text-right tabular-nums text-slate-700"
                            >
                              {formatValue(cellFor(row, group, metric.key), metric.unit)}
                            </td>
                          ))}
                        </tr>
                      ))}
                      <tr className="bg-slate-50/80 font-semibold">
                        <td className="px-4 py-3 text-slate-900">All branches</td>
                        {metrics.map((metric) => (
                          <td
                            key={metric.key}
                            className="px-3 py-3 text-right tabular-nums text-slate-900"
                          >
                            {formatValue(cellFor(data.totals, group, metric.key), metric.unit)}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}
